import { useHistory } from 'react-router-dom';
import classnames from 'classnames';

import { ReactComponent as TeleconsultationSVG } from 'icons/sd-tc.svg';
import { ReactComponent as CheckCircleSVG } from 'icons/table/check-circle.svg';
import { ReactComponent as ClockSVG } from 'icons/table/clock.svg';

import { getHintFromPaymentStatus } from 'helpers/hint';
import useWindowDimensions, {
  isMobileResolution,
} from 'helpers/useWindowDimensions';

import { STAGES, STATUSES } from 'config/teleconsultation';

import { compareDesc } from 'date-fns';
import { timeInParis } from 'helpers/timezone';

const TeleconsultationCardNew = ({ data, date, userType }) => {
  const history = useHistory();
  const { width } = useWindowDimensions();
  const isMobile = isMobileResolution(width);

  const { id, nurse, doctor, stage, status, cancelled, paymentIntents } = data;

  const patientPaymentIntent = paymentIntents?.filter(
    ({ type }) => type === 'patient',
  );

  const succeededPayment = patientPaymentIntent?.some(
    ({ status }) => status === 'succeeded',
  );
  const intent = succeededPayment
    ? patientPaymentIntent
        .filter(({ status }) => status === 'succeeded')
        .sort((a, b) =>
          compareDesc(timeInParis(a.createdAt), timeInParis(b.createdAt)),
        )
    : patientPaymentIntent?.sort((a, b) =>
        compareDesc(timeInParis(a.createdAt), timeInParis(b.createdAt)),
      );

  let statusLabel;

  const paymentClasses = classnames('th-badge mr5', {
    'th-badge-green': succeededPayment,
    'th-badge-grey': !!succeededPayment,
  });

  const statusClasses = classnames('th-badge mr5', {
    'th-badge-green': status === 'conducted',
    'th-badge-pink': [
      'late-cancellation-by-patient',
      'timely-cancellation-by-patient',
      'cancelled-by-host',
      'no-show',
    ].includes(status),
  });

  const articleClasses = classnames('grid-cards', {
    pointer: userType !== 'non-sleep',
  });

  const stageTextClasses = classnames('apps-card-app-title-new', {
    'strike-through': cancelled,
  });

  const startTimeClasses = classnames('apps-card-app-desc-new', {
    'strike-through': cancelled,
  });

  const icon = succeededPayment ? <CheckCircleSVG /> : <ClockSVG />;

  const generateFullName = (user) =>
    user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : null;

  if (
    [
      'late-cancellation-by-patient',
      'timely-cancellation-by-patient',
      'cancelled-by-host',
    ].includes(status)
  ) {
    statusLabel = 'cancelled';
  } else {
    statusLabel = STATUSES.find((s) => s.id === status)?.label;
  }

  const DesktopView = (
    <article
      className={articleClasses}
      onClick={() =>
        userType === 'non-sleep'
          ? null
          : history.push(`/teleconsultations/${id}`)
      }
    >
      <div className="apps-card-left-new" style={{ gridColumn: 'span 6' }}>
        <div className="mr6">
          <TeleconsultationSVG />
        </div>
        <div className="apps-card-meta">
          <div className={stageTextClasses}>
            {STAGES.find((s) => s.id === stage)?.label.includes(
              // a retarded piece of code I am not proud of
              'teleconsultation',
            )
              ? STAGES.find((s) => s.id === stage)?.label.replace(
                  'teleconsultation',
                  '',
                )
              : STAGES.find((s) => s.id === stage)?.label}{' '}
            teleconsultation
          </div>
          <div className="flex flex-row" style={{ gap: '6px' }}>
            <div>
              {doctor && <p className="apps-card-app-desc-new">Doctor:</p>}
              {nurse && <p className="apps-card-app-desc-new">Nurse:</p>}
            </div>
            <div className="flex flex-column">
              {doctor && (
                <p className="apps-card-app-desc-new">
                  {doctor?.lastName?.toUpperCase()} {doctor?.firstName}
                </p>
              )}
              {nurse && (
                <p className="apps-card-app-desc-new">
                  {generateFullName(nurse)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="apps-card-right-new" style={{ gridColumn: 'span 2' }}>
        {status && <span className={statusClasses}>{statusLabel}</span>}
      </div>
      <div className="apps-card-right-new" style={{ gridColumn: 'span 2' }}>
        {paymentIntents?.length > 0 && userType !== 'non-sleep' && (
          <span
            className={paymentClasses}
            data-rh={
              getHintFromPaymentStatus[
                succeededPayment ? 'succeeded' : 'incomplete'
              ]
            }
          >
            {icon}€{intent[0]?.amount}
          </span>
        )}
      </div>

      <div
        className="apps-card-right-new"
        style={{ gridColumn: 'span 2', justifySelf: 'end' }}
      >
        <div>
          <p className={startTimeClasses}>Start time:</p>
          <p className={startTimeClasses}>{date}</p>
        </div>
      </div>
    </article>
  );

  const MobileView = (
    <article
      className={articleClasses}
      onClick={() =>
        userType === 'non-sleep'
          ? null
          : history.push(`/teleconsultations/${id}`)
      }
    >
      <div className="apps-card-left-new" style={{ gridColumn: 'span 10' }}>
        <div className="apps-card-meta ">
          <div className={stageTextClasses}>
            {STAGES.find((s) => s.id === stage)?.label.includes(
              // a retarded piece of code I am not proud of
              'teleconsultation',
            )
              ? STAGES.find((s) => s.id === stage)?.label.replace(
                  'teleconsultation',
                  '',
                )
              : STAGES.find((s) => s.id === stage)?.label}{' '}
            teleconsultation
          </div>
          {doctor && (
            <div className="flex flex-row" style={{ gap: '6px' }}>
              <div>
                <p className="apps-card-app-desc-new">Doctor:</p>
              </div>
              <div className="flex flex-column">
                <p className="apps-card-app-desc-new">
                  {doctor?.lastName?.toUpperCase()} {doctor?.firstName}
                </p>
              </div>
            </div>
          )}
          {nurse && (
            <div className="flex flex-row" style={{ gap: '6px' }}>
              <div>
                <p className="apps-card-app-desc-new">Nurse:</p>
              </div>
              <div className="flex flex-column">
                <p className="apps-card-app-desc-new">
                  {generateFullName(nurse)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className="apps-card-right-new"
        style={{ gridColumn: 'span 2', justifySelf: 'end' }}
      >
        <div>
          <p className={startTimeClasses}>Start time:</p>
          <p className={startTimeClasses}>{date}</p>
        </div>
      </div>

      <div
        className="apps-card-right-new"
        style={{ gridColumn: 'span 6', gap: '8px' }}
      >
        {status && <span className={statusClasses}>{statusLabel}</span>}
        {paymentIntents?.length > 0 && userType !== 'non-sleep' && (
          <span
            className={paymentClasses}
            data-rh={
              getHintFromPaymentStatus[
                succeededPayment ? 'succeeded' : 'incomplete'
              ]
            }
          >
            {icon}€{intent[0]?.amount}
          </span>
        )}
      </div>
    </article>
  );

  return isMobile ? MobileView : DesktopView;
};

export default TeleconsultationCardNew;
